import { useEffect, useState } from "react";
import useDataAccessApi from "../../Hooks/useDataAccessApi";
import { Button, ResultValue } from "../Simple";
import ApiError from "./ApiError";
import useCancelBatchApi from "../../Hooks/useCancelBatchApi";
import { format } from "date-fns";
import useDeleteBatchApi from "../../Hooks/useDeleteBatchApi";
import { enableDeletion } from '../../Utils/features';


const BatchInfo = (props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [status, setStatus] = useState('');
    const [rowCount, setRowCount] = useState();
    const [completedPreprocessing, setCompletedPreprocessing] = useState();
    const [completedPostprocessing, setCompletedPostprocessing] = useState();
    const [started, setStarted] = useState();
    const [finished, setFinished] = useState();
    const [pricerCounts, setPricerCounts] = useState([]);
    const [showCancelModal, setCancelShowModal] = useState(false);
    const [showDeleteModal, setDeleteShowModal] = useState(false);
    const cancelBatch = useCancelBatchApi();
    const deleteBatch = useDeleteBatchApi();
    const dataAccess = useDataAccessApi();

    useEffect(() => {
        const { uploads, postProcessingComplete, outputAggregationComplete, batchComplete, batchCanceled, batchDeleted } = props.batch;
        if (batchCanceled) {
            setStatus('Canceled');
            return;
        }

        if (batchDeleted) {
            setStatus('Deleted');
            return;
        }

        if (batchComplete) {
            setStatus('Complete');
            return;
        }

        if (!uploads || uploads.length === 0) {
            setStatus('Loading');
            return;
        }

        if (postProcessingComplete && outputAggregationComplete) {
            setStatus('Bundling');
            return;
        }

        setStatus('Processing');
    }, [props.batch]);

    useEffect(() => {
        const { uploads } = props.batch;

        if (!uploads || uploads.length === 0) {
            return;
        }

        setRowCount(uploads.reduce((pv, cv) => pv + cv.totalRows, 0));
    }, [props.batch]);

    useEffect(() => {
        const { uploads, startedOn, finishedOn } = props.batch;

        if (!uploads || uploads.length === 0) {
            return;
        }

        setCompletedPreprocessing(uploads.reduce((pv, cv) => pv + cv.completedPreprocessing, 0));
        setCompletedPostprocessing(uploads.reduce((pv, cv) => pv + cv.completedPostprocessing, 0));
        setStarted(startedOn && format(new Date(startedOn), 'MM/dd/yyyy hh:mm:ss aa'));
        setFinished(finishedOn && format(new Date(finishedOn), 'MM/dd/yyyy hh:mm:ss aa'))
    }, [props.batch]);

    const { batchId } = props.batch;

    const handleOnCancel = async () => {
        await cancelBatch.execute(batchId);
        if (props.onCancel) {
            props.onCancel(batchId);
        }
    };

    const handleOnDelete = async () => {
        if (enableDeletion) {
            await deleteBatch.execute(batchId);
            if (props.onDelete) {
                props.onDelete(batchId);
            }
        }
    };

    const handleDownloadWithBatchId = async () => {
        const file = await dataAccess.download(batchId);
        if (!file) {
            // nothing came back so we probably errored
            return;
        }
        const blob = new Blob([file], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', batchId + '.zip');
        link.click();

        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 1000);
    }

    const togglePanel = () => {
        setIsOpen(o => !o);
    };

    const closeCancelModal = () => {
        setCancelShowModal(false);
    }

    const closeDeleteModal = () => {
        setDeleteShowModal(false)
    }
    

    return (
        <div className="batch-info-container">
            <ApiError error={dataAccess.error || cancelBatch.error} />
            <div className="row align-items-center">
                <div className="col-auto">
                    <Button icon={isOpen ? <i className="bi bi-chevron-up"></i> : <i className="bi bi-chevron-down"></i>}
                        iconLocation="start"
                        onClick={togglePanel}
                        className="dropdown-button" />
                </div>
                <div className="col-4">
                    <strong>Batch ID:</strong> {batchId}
                </div>
                <div className="col">
                    <strong>Status:</strong> {status}
                </div>
                <div className="col">
                    <strong>Row Count:</strong> {rowCount}
                </div>
                <div className="col">
                    <div className="row justify-content-end">
                        {status === "Complete" ? (
                            <div className="col-auto">
                            <Button id={`download-batch-button-${batchId}`}
                                className="btn-primary mr-2"  
                                onClick={handleDownloadWithBatchId}
                                text="Download"
                                disabled={dataAccess.downloading}
                                loading={dataAccess.downloading} />
                            { enableDeletion && (
                            <Button id={`delete-batch-button-${batchId}`}
                                className="btn-danger"
                                onClick={() => setDeleteShowModal(true)}
                                text="Delete"
                                disabled={deleteBatch.loading}
                                loading={deleteBatch.loading} />
                            )}
                            </div>
                        ) : (
                            (status !== "Canceled" && (status !== "Deleted" && enableDeletion)) && <div className="col-auto">
                                <Button text="Cancel"
                                id={`cancel-batch-button-${batchId}`}
                                className="btn-warning"
                                loading={cancelBatch.loading}
                                disabled={cancelBatch.loading}
                                onClick={() => setCancelShowModal(true)} />
                            </div>  
                        )}

                        
                        {/* This simply helps with visibility of modal */}
                        {showCancelModal && <div className="modal-backdrop fade show" style={{marginTop: '60px'}}> </div>}
                        {showDeleteModal && <div className="modal-backdrop fade show" style={{marginTop: '60px'}}> </div>}
                        
                        <div className={`modal ${showCancelModal ? 'show' : ''} fade show`} id="cancelModal" tabIndex="-1" aria-labelledby="cancelModalLabel" aria-hidden="true" style={showCancelModal ? {display: 'block', top: '60px'} : {display: 'none', top: '60px'}}>
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="cancelModalLabel">Cancel Batch</h5>
                                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                    </div>
                                    <div className="modal-body">
                                        Are you sure you want to cancel this batch?
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={closeCancelModal}>Close</button>
                                        <button type="button" className="btn btn-danger" onClick={() => { closeCancelModal(); handleOnCancel(); }}>Confirm Cancel</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {<div className={`modal ${showDeleteModal ? 'show' : ''} fade show`} id="deleteModal" tabIndex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true" style={showDeleteModal ? {display: 'block', top: '60px'} : {display: 'none', top: '60px'}}>
                            <div className="modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title" id="deleteModalLabel">Delete Batch</h5>
                                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                    </div>
                                    <div className="modal-body">
                                        Are you sure you want to delete this batch?
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={closeDeleteModal}>Close</button>
                                        <button type="button" className="btn btn-danger" onClick={() => { closeDeleteModal(); handleOnDelete(); }}>Confirm Delete</button>
                                    </div>
                                </div>
                            </div>
                        </div>}
                        
                    </div>
                </div>
            </div>
            {isOpen && (
                <div className="row padding-5">
                    <div className="col">
                        <hr />
                        <div className="row">
                            <div className="col">
                                <h4>Batch Details</h4>
                                <div className="row">
                                    <div className="col-lg-6">
                                        <ResultValue label="Rows Loaded:" value={completedPreprocessing} />
                                        <ResultValue label="Rows Processed:" value={completedPostprocessing} />
                                        
                                        {pricerCounts && pricerCounts.length > 0 && <h5>Pricer Counts</h5>}
                                        {pricerCounts.map(c => {
                                            return <ResultValue label={c.pricer} value={c.total} />
                                        })}
                                    </div>
                                    <div className="col-lg-6">
                                        <ResultValue label="Started:" value={started} />
                                        <ResultValue label="Finished:" value={finished} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BatchInfo;